import { createContext, useCallback, useContext, useEffect, useReducer, useMemo, useRef } from "react";
import * as cartTypes from "./ContextTypes";
import cartReducer from "./cartReducer";
import { useSelector, useDispatch } from "react-redux";
import { getShoppingCartItems } from "../api/action/cart";
import { setShoppingCartData } from "../api/reducers/shoppingCartReducer";
import { academyLogger } from "utils/logger";
import { useSiteContext } from "./SiteContext.jsx";
import { useAuthSession } from "../auth/useAuthSession.js";

// initialising context
const CartContext = createContext(null);



const getLocalCartData = () => {
  const localCartData = localStorage.getItem("cartProducts");
  if (localCartData < 1) {
    return [];
  } else {
    return JSON.parse(localCartData);
  }
};
const getTotalItems = () => {
  let localCartData = window.localStorage.getItem("cartProducts");
  if (localCartData < 1) {
    return 0;
  } else {
    let totalQty = JSON.parse(localCartData)?.reduce((initialVal, currElem) => {
      initialVal += currElem.quantity;
      return initialVal;
    }, 0);
    return totalQty;
  }
};

const initialState = {
  cartProducts: getLocalCartData(),
  total_items: getTotalItems(),
  total_price: "",
  shipping_fee: 0,
};
const cartRequests = new Map();

const fetchCartOnce = (shoppingCartId) => {
  const cacheKey = String(shoppingCartId || "");
  if (!cacheKey) return Promise.resolve([]);

  if (cartRequests.has(cacheKey)) {
    return cartRequests.get(cacheKey);
  }

  const request = getShoppingCartItems(shoppingCartId).finally(() => {
    cartRequests.delete(cacheKey);
  });
  cartRequests.set(cacheKey, request);
  return request;
};

const CartProvider = ({ children }) => {

  const [cartState, dispatch] = useReducer(cartReducer, initialState);
  const reduxDispatch = useDispatch();
  const authState = useSelector((state) => state?.auth?.data);
  const siteContext = useSiteContext();
  const authSession = useAuthSession();

  const addToCart = useCallback((item) => {
    dispatch({ type: cartTypes.ADD_TO_CART, payload: item });
  }, []);
  const updateCart = useCallback((item) => {
    dispatch({ type: cartTypes.UPDATE_TO_CART, payload: item });
  }, []);
  const removeCartItem = useCallback((id) => {
    dispatch({ type: cartTypes.REMOVE_FROM_CART, payload: id });
  }, []);
  // to clear the cart
  const clearCart = useCallback(() => {
    dispatch({ type: cartTypes.EMPTY_CART });
  }, []);
  const setIncrease = useCallback((id) => {
    dispatch({ type: cartTypes.SET_INCREMENT, payload: id });
  }, []);
  const setDecrease = useCallback((id) => {
    dispatch({ type: cartTypes.SET_DECREMENT, payload: id });
  }, []);
  const companyDetails = useSelector((state) => state?.companyReducer?.company);
  const companyDomain = companyDetails?.domain?.type || '';
  const isAuthenticated = useSelector((state) => state?.auth?.isAuthenticated);
  const wasAuthenticatedRef = useRef(Boolean(isAuthenticated));
  const cartOwnerKey = useMemo(() => {
    const accountId = authSession.user?.account?.primaryId
      || authSession.user?.primaryId
      || authState?.account?.primaryId
      || "guest";
    return siteContext?.siteKey ? `${siteContext.siteKey}:${accountId}` : "";
  }, [authSession.user, authState, siteContext?.siteKey]);

  useEffect(() => {
    if (!cartOwnerKey) return;

    const ownerStorageKey = "academy:cart-owner:v2";
    const previousOwner = localStorage.getItem(ownerStorageKey);
    if (previousOwner && previousOwner !== cartOwnerKey) {
      dispatch({ type: cartTypes.EMPTY_CART });
      localStorage.removeItem("cartProducts");
      localStorage.removeItem("shoppingCartData");
      reduxDispatch(setShoppingCartData([]));
    }
    localStorage.setItem(ownerStorageKey, cartOwnerKey);
  }, [cartOwnerKey, reduxDispatch]);

  useEffect(() => {
    const wasAuthenticated = wasAuthenticatedRef.current;
    if (wasAuthenticated && !isAuthenticated) {
      dispatch({ type: cartTypes.EMPTY_CART });
      localStorage.removeItem("cartProducts");
      localStorage.removeItem("shoppingCartData");
      localStorage.removeItem("academy:cart-owner:v2");
      reduxDispatch(setShoppingCartData([]));
    }
    wasAuthenticatedRef.current = Boolean(isAuthenticated);
  }, [isAuthenticated, reduxDispatch]);
  const getAccountDetails = useCallback(async () => {

    if (!isAuthenticated) return;
    if (companyDomain === "FLEX_OFFICE") return;

    try {
      const userAccount = authState;
      const accountId = userAccount?.account?.primaryId;

      if (!accountId) {
        academyLogger.error("Account ID is null or undefined");
        return;
      }

      const shoppingCartId = userAccount.account?.shoppingCart?.primaryId;
      if (!shoppingCartId) {
        return;
      }

      const shoppingCartData = await fetchCartOnce(shoppingCartId);
      // localStorage.setItem("shoppingCartData", JSON.stringify(shoppingCartData));
      if (shoppingCartData?.[0]) {
        reduxDispatch(setShoppingCartData(shoppingCartData[0]));
      }

      // Only update cartProducts if it changes
      const existingCartProducts = JSON.parse(localStorage.getItem("cartProducts")) || [];
      const serverCartProducts = shoppingCartData?.[0]?.productList || [];
      if (JSON.stringify(existingCartProducts) !== JSON.stringify(serverCartProducts)) {
        localStorage.setItem("cartProducts", JSON.stringify(serverCartProducts));
        dispatch({ type: cartTypes.UPDATE_TO_CART, payload: serverCartProducts });
      }
    } catch (error) {
      academyLogger.error("Error fetching account details:", error);
    }
  }, [authState, companyDomain, isAuthenticated, reduxDispatch]);

  useEffect(() => {
    getAccountDetails();
  }, [getAccountDetails]);

  //to add the data in localstorage
  useEffect(() => {
    const existingCartProducts = JSON.parse(localStorage.getItem("cartProducts")) || [];
    if (JSON.stringify(existingCartProducts) !== JSON.stringify(cartState.cartProducts)) {
      localStorage.setItem("cartProducts", JSON.stringify(cartState.cartProducts));
      // dispatch(setCartProducts(cartState.cartProducts));
    }
  }, [cartState.cartProducts]);

  return (
    <CartContext.Provider
      value={{
        ...cartState,
        dispatch,
        addToCart,
        removeCartItem,
        clearCart,
        setIncrease,
        setDecrease,
        updateCart
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  return useContext(CartContext);
};

export default CartProvider;
